import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './utils';

import type { TAgentResponse as TSystemAgentResponse } from '@/types/agents';
import type { IGetCustomerAgentOutputDto, TCreateAgentRunConfigDto, TGetCustomerAgentsDto, TUpdateAgentRunConfigDto } from '@/types/agentsAgentDesk';

type AgentProperties = Record<string, string | number | boolean | null | string[]>;

export const agentsApi = createApi({
  reducerPath: 'agentsApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    getAgents: build.query({
      query: (params: { page: number; limit: number; search?: string; customerIds?: string[] }) => ({
        url: 'agent/get-agents',
        method: 'POST',
        body: {
          ...params,
          search: params.search || '',
        },
        transformResponse: (response: TSystemAgentResponse) => response,
      }),
    }),
    getAgent: build.query({
      query: (referenceId: string) => ({
        url: 'agent',
        method: 'GET',
        params: { referenceId },
      }),
    }),
    assignAgentToCustomers: build.mutation({
      query: (params: { agentId: string; customerIds: string[] }) => ({
        url: 'agent/assign-agent',
        method: 'POST',
        body: params,
        transformResponse: (response: TSystemAgentResponse) => response,
      }),
    }),
    getCredentialTypes: build.query({
      query: () => ({
        url: 'agent/get-credential-types',
        method: 'POST',
      }),
    }),

    getCustomerAgents: build.query<
      {
        items: IGetCustomerAgentOutputDto[];
        pagination: { page: number; limit: number; totalPages: number; total?: number };
        healthyAgents: number;
        warningAgents: number;
        criticalAgents: number;
      },
      TGetCustomerAgentsDto | void
    >({
      query: (params) => ({
        url: 'agent/get-customer-agents',
        method: 'POST',
        body: {
          page: 1,
          limit: 25,
          ...params,
        },
      }),
    }),
    getCustomerAgentById: build.query({
      query: (referenceId: string) => ({
        url: 'agent/customer-agent/',
        method: 'GET',
        params: {
          referenceId,
        },
      }),
    }),
    createCustomerAgent: build.mutation({
      query: (params: { customerId: string; agentId: string; credentialIds: string[] }) => ({
        url: 'agent/customer-agent',
        method: 'POST',
        body: params,
      }),
    }),
    createCustomerAgentAssignment: build.mutation({
      query: (params: { agentId: string; agentRunConfigId: string; name: string; userIds: string[] }) => ({
        url: 'agent/create-customer-agent',
        method: 'POST',
        body: params,
      }),
    }),
    updateCustomerAgent: build.mutation({
      query: (params: { referenceId: string; credentialIds: string[] }) => ({
        url: 'agent/customer-agent',
        method: 'PATCH',
        body: params,
      }),
    }),
    updateCustomerAgentAssignment: build.mutation({
      query: (params: { referenceId: string; agentRunConfigId: string; name: string; users: string[] }) => ({
        url: 'agent/update-customer-agent',
        method: 'PATCH',
        body: params,
      }),
    }),
    deleteCustomerAgentAssignment: build.mutation<void, { referenceId: string }>({
      query: ({ referenceId }) => ({
        url: 'agent/delete-customer-agent',
        method: 'DELETE',
        params: { referenceId },
      }),
    }),
    toggleCustomerAgentActive: build.mutation<void, { referenceId: string }>({
      query: ({ referenceId }) => ({
        url: 'agent/toggle-customer-agent-active',
        method: 'PATCH',
        params: { referenceId },
      }),
    }),
    runAgent: build.mutation({
      query: (params: { referenceId: string; properties: AgentProperties }) => ({
        url: 'agent/run-agent',
        method: 'POST',
        body: params,
        responseHandler: (response) => response.text(),
      }),
    }),
    getAgentRunConfigList: build.query({
      query: (agentId: string) => ({
        url: 'run-config/agent-run-configs',
        method: 'GET',
        params: { agentId },
      }),
    }),
    createAgentRunConfig: build.mutation({
      query: (params: TCreateAgentRunConfigDto) => ({
        url: 'run-config/agent-run-config',
        method: 'POST',
        body: params,
      }),
    }),
    updateAgentRunConfig: build.mutation({
      query: (params: TUpdateAgentRunConfigDto) => ({
        url: 'run-config/agent-run-config',
        method: 'PATCH',
        body: params,
      }),
    }),
  }),
});

export const {
  useGetAgentsQuery,
  useLazyGetAgentQuery,
  useAssignAgentToCustomersMutation,
  useGetCredentialTypesQuery,
  useGetCustomerAgentsQuery,
  useGetCustomerAgentByIdQuery,
  useCreateCustomerAgentMutation,
  useCreateCustomerAgentAssignmentMutation,
  useUpdateCustomerAgentMutation,
  useUpdateCustomerAgentAssignmentMutation,
  useDeleteCustomerAgentAssignmentMutation,
  useToggleCustomerAgentActiveMutation,
  useRunAgentMutation,
  useLazyGetAgentRunConfigListQuery,
  useCreateAgentRunConfigMutation,
  useUpdateAgentRunConfigMutation,
} = agentsApi;
